
import React, { useState, useEffect, useRef } from 'react';
import Sidebar from './components/Sidebar';
import Dashboard from './components/Dashboard';
import TeamManagement from './components/TeamManagement';
import GameTime from './components/GameTime';
import PracticeDrills from './components/PracticeDrills';
import Schedule from './components/Schedule';
import Stats from './components/Stats';
import Auth from './components/Auth';
import BetaAccessRequest from './components/BetaAccessRequest';
import OrgOnboarding from './components/OrgOnboarding';
import OrgSettings from './components/OrgSettings';
import FeedbackButton from './components/FeedbackButton';
import WelcomeTourModal from './components/onboarding/WelcomeTourModal';
import PendingApprovalScreen from './components/PendingApprovalScreen';
import SectionBanner from './components/onboarding/SectionBanner';
import { ViewState, Player, Team, GameEvent, UserProfile, Organization, Lineup, AppSettings, MatchRecord, Season } from './types';
import { identifyUser, resetPostHog } from './providers/PostHogProvider';
import { setSentryUser, clearSentryUser } from './providers/SentryProvider';
import { Menu, Loader2, LogOut, Bell, ArrowRight, ChevronDown, AlertTriangle } from 'lucide-react';
import { supabase, mapTeamFromDB, mapPlayerFromDB, mapEventFromDB, mapProfileFromDB, mapOrganizationFromDB, mapLineupFromDB, mapSeasonFromDB, getMatchRecords, getPendingApprovalCount, getPreferredTeamId, setPreferredTeamId, saveUserSettings } from './services/supabase';
import { getOnboardingState, saveOnboardingState, markSectionShown, resetOnboarding, OnboardingState, OnboardingSection } from './lib/onboardingState';
import BetaBanner from './components/BetaBanner';
import CsatBanner from './components/CsatBanner';
import UnitPreferenceModal, { hasCompletedUnitOnboarding } from './components/UnitPreferenceModal';
import CookieBanner from './components/CookieBanner';
import { getConsentState } from './lib/analytics';

// Capture the OAuth code at module-init time — before React renders and before
// the view-URL-sync effect can call history.replaceState and wipe the ?code= param.
const _oauthCode = new URLSearchParams(window.location.search).get('code');
if (_oauthCode) {
  window.history.replaceState(null, '', window.location.pathname);
}

const App: React.FC = () => {
  // Auth State
  // Read ?join=CODE and ?mode=signup from URL on mount
  const [pendingJoinCode] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('join');
    if (code) window.history.replaceState({}, '', window.location.pathname);
    return code;
  });

  const [defaultAuthMode] = useState<'login' | 'signup'>(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('mode') === 'signup' ? 'signup' : 'login';
  });

  const [session, setSession] = useState<any>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [isLoadingAuth, setIsLoadingAuth] = useState(true);
  const [isLoadingOrgs, setIsLoadingOrgs] = useState(false);
  const [isSigningOut, setIsSigningOut] = useState(false);

  // Use a Ref to track profile state to avoid stale closures in event listeners
  const userProfileRef = useRef<UserProfile | null>(null);
  // Add a Ref to prevent duplicate fetch calls
  const isFetchingProfileRef = useRef(false);
  
  // Org State
  const [myOrgs, setMyOrgs] = useState<Organization[]>([]);
  const [currentOrg, setCurrentOrg] = useState<Organization | null>(null);

  // App State — initialize from URL path so direct navigation works
  const [currentView, setCurrentView] = useState<ViewState>(() => {
    const pathToView: Record<string, ViewState> = {
      '/dashboard':       ViewState.DASHBOARD,
      '/stats':           ViewState.STATS,
      '/schedule':        ViewState.SCHEDULE,
      '/team-management': ViewState.TEAM,
      '/game-time':       ViewState.GAME,
      '/practice':        ViewState.PRACTICE,
      '/settings':        ViewState.SETTINGS,
    };
    return pathToView[window.location.pathname] ?? ViewState.DASHBOARD;
  });
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [shouldAutoCreateTeam, setShouldAutoCreateTeam] = useState(false);
  const [activeStatsEventId, setActiveStatsEventId] = useState<string>('all');
  const [activeGameEventId, setActiveGameEventId] = useState<string | undefined>(undefined);
  const [activePracticeDate, setActivePracticeDate] = useState<string | undefined>(undefined);
  const [globalActiveTeamId, setGlobalActiveTeamId] = useState<string>('');

  // Stats editing guard
  const [statsIsEditing, setStatsIsEditing] = useState(false);
  const statsLeaveRequestRef = useRef<((targetView: number) => void) | null>(null);

  // Practice plan unsaved changes guard
  const [practiceIsDirty, setPracticeIsDirty] = useState(false);
  const practiceLeaveRequestRef = useRef<((targetView: number) => void) | null>(null);

  const handleSetView = (view: ViewState) => {
    if (currentView === ViewState.STATS && statsIsEditing && view !== ViewState.STATS) {
      statsLeaveRequestRef.current?.(view);
    } else if (currentView === ViewState.PRACTICE && practiceIsDirty && view !== ViewState.PRACTICE) {
      practiceLeaveRequestRef.current?.(view);
    } else {
      setCurrentView(view);
    }
  };

  // Keep URL in sync with current view
  useEffect(() => {
    const viewToPath: Record<ViewState, string> = {
      [ViewState.DASHBOARD]:    '/dashboard',
      [ViewState.STATS]:        '/stats',
      [ViewState.SCHEDULE]:     '/schedule',
      [ViewState.TEAM]:         '/team-management',
      [ViewState.GAME]:         '/game-time',
      [ViewState.PRACTICE]:     '/practice',
      [ViewState.SETTINGS]:     '/settings',
      [ViewState.ORGANIZATION]: '/settings',
    };
    const path = viewToPath[currentView] ?? '/dashboard';
    if (window.location.pathname !== path) {
      window.history.pushState({}, '', path);
    }
  }, [currentView]);

  // Pending approvals (admin only)
  const [pendingApprovalsCount, setPendingApprovalsCount] = useState(0);
  const [showPendingPopup, setShowPendingPopup] = useState(false);
  const [orgSettingsInitialTab, setOrgSettingsInitialTab] = useState<string | undefined>(undefined);
  const hasShownPendingPopupRef = useRef(false);

  const [teams, setTeams] = useState<Team[]>([]);
  const [players, setPlayers] = useState<Player[]>([]);
  const [events, setEvents] = useState<GameEvent[]>([]);
  const [lineups, setLineups] = useState<Lineup[]>([]);
  const [matchRecords, setMatchRecords] = useState<MatchRecord[]>([]);
  const [seasons, setSeasons] = useState<Season[]>([]);
  const [viewingSeason, setViewingSeason] = useState<Season | null>(null);

  // Global Settings
  const [appSettings, setAppSettings] = useState<AppSettings>(() => {
     const defaults: AppSettings = { substitutionsPerSet: 12, measurementSystem: 'imperial', simpleStats: true, allowTwoLiberos: false };
     const saved = localStorage.getItem('sideoutiq_settings');
     return saved ? { ...defaults, ...JSON.parse(saved) } : defaults;
  });

  // Effective unit system: user personal override → org default → 'imperial'
  // Components should use this derived value, not appSettings.measurementSystem directly.
  const effectiveUnitSystem: 'imperial' | 'metric' =
    appSettings.measurementSystem ?? currentOrg?.unitPreference ?? 'imperial';

  // Cookie consent — null means first visit (show banner)
  const [cookieConsent, setCookieConsent] = useState<'accepted' | 'declined' | null>(getConsentState);

  // Unit preference onboarding — show once per org for the org creator/admin
  const [showUnitModal, setShowUnitModal] = useState(false);
  useEffect(() => {
    if (!currentOrg?.id || currentOrg.id === 'dev-org-id') return;
    setShowUnitModal(!hasCompletedUnitOnboarding(currentOrg.id));
  }, [currentOrg?.id]);

  // Onboarding state
  const [onboardingState, setOnboardingState] = useState<OnboardingState>({ welcomeComplete: false, sectionsShown: [], tourComplete: false, demoGameDone: true });
  // Stays false until the onboarding useEffect has loaded real state from localStorage,
  // preventing a flash of the welcome modal on every app load for existing users.
  const [isOnboardingStateReady, setIsOnboardingStateReady] = useState(false);
  const prevMatchRecordsLenRef = useRef(0);
  const hasExistingDataRef = useRef(false);

  useEffect(() => {
     localStorage.setItem('sideoutiq_settings', JSON.stringify(appSettings));
  }, [appSettings]);

  // Debounce-sync settings to Supabase so they persist across devices
  const settingsSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    const userId = userProfile?.id;
    if (!userId) return;
    if (settingsSaveTimerRef.current) clearTimeout(settingsSaveTimerRef.current);
    settingsSaveTimerRef.current = setTimeout(() => {
      saveUserSettings(userId, appSettings);
    }, 1500);
    return () => { if (settingsSaveTimerRef.current) clearTimeout(settingsSaveTimerRef.current); };
  }, [appSettings, userProfile?.id]);

  // Load onboarding state when user + org are known
  useEffect(() => {
    if (!userProfile?.id || !currentOrg?.id) return;
    const state = getOnboardingState(userProfile.id, currentOrg.id);
    setOnboardingState(state);
    setIsOnboardingStateReady(true);
    prevMatchRecordsLenRef.current = 0;
    hasExistingDataRef.current = false;
  }, [userProfile?.id, currentOrg?.id]);

  // Skip onboarding entirely for existing users (has roster or game history)
  useEffect(() => {
    if (!userProfile?.id || !currentOrg?.id) return;
    const isExistingUser = players.length > 0 || matchRecords.length > 0;
    if (isExistingUser && !hasExistingDataRef.current) {
      hasExistingDataRef.current = true;
      setOnboardingState(prev => {
        if (prev.welcomeComplete && prev.demoGameDone) return prev;
        const ALL_SECTIONS: OnboardingSection[] = ['team', 'schedule', 'gametime', 'stats', 'org', 'practice'];
        const next: OnboardingState = { welcomeComplete: true, sectionsShown: ALL_SECTIONS, tourComplete: true, demoGameDone: true };
        saveOnboardingState(userProfile.id, currentOrg.id, next);
        return next;
      });
    }
  }, [players.length, matchRecords.length, userProfile?.id, currentOrg?.id]);

  useEffect(() => {
    prevMatchRecordsLenRef.current = matchRecords.length;
  }, [matchRecords.length]);
  
  // Data Loading State
  const [isDevDataLoaded, setIsDevDataLoaded] = useState(false);
  // True while the initial org data fetch (teams/players/events/etc.) is in flight.
  // Prevents the dashboard from rendering with empty arrays before data arrives.
  const [isLoadingData, setIsLoadingData] = useState(false);

  console.log("SideoutIQ: App component evaluating.");

  // Keep ref in sync with state
  useEffect(() => {
    userProfileRef.current = userProfile;
  }, [userProfile]);

  // 1. Check Auth & Load Profile
  useEffect(() => {
    let mounted = true;

    const fetchProfile = async (currentSession: any) => {
      if (isFetchingProfileRef.current) return;
      if (!currentSession?.user?.id) {
        setSession(null);
        setIsLoadingAuth(false);
        return;
      }

      isFetchingProfileRef.current = true;

      // Safety timeout scoped to this fetch attempt. The old mount-level timeout
      // fired once at startup and was gone — if the user took >8s to log in,
      // SIGNED_IN would start a hang with nothing to rescue it. This per-fetch
      // timeout resets every time fetchProfile is called.
      const fetchTimeout = setTimeout(() => {
        if (mounted) {
          console.warn("Auth profile fetch timed out.");
          setIsLoadingAuth(false);
          isFetchingProfileRef.current = false;
        }
      }, 8000);

      try {
        const userId = currentSession.user.id;
        let profile: UserProfile | null = null;

        try {
          const queryPromise = supabase.from('profiles').select('*').eq('id', userId).single();
          const timeoutPromise = new Promise<never>((_, reject) =>
            setTimeout(() => reject(new Error('Profile query timed out')), 4000)
          );
          const { data, error } = await Promise.race([queryPromise, timeoutPromise]);
          if (data && !error) {
            profile = mapProfileFromDB(data);
          }
        } catch (dbError) {
          console.warn("Profile DB fetch failed (might be offline):", dbError);
        }

        if (!profile) {
          profile = {
            id: userId,
            email: currentSession.user.email,
            fullName: currentSession.user.user_metadata?.full_name || 'Coach'
          };
        }

        if (mounted) {
          userProfileRef.current = profile; // Set synchronously to prevent re-trigger race
          setUserProfile(profile);
          // Merge DB-saved settings into local state (DB wins over localStorage)
          if (profile.appSettings && Object.keys(profile.appSettings).length > 0) {
            setAppSettings(prev => ({ ...prev, ...profile!.appSettings }));
          }
          identifyUser(profile.id, { email: profile.email, name: profile.fullName });
          setSentryUser(profile.id, profile.email);
          setIsLoadingOrgs(true);
          fetchMyOrgs(userId); // Not awaited — isLoadingOrgs tracks completion separately
        }

      } catch (e) {
        console.error('Critical error fetching profile:', e);
      } finally {
        clearTimeout(fetchTimeout);
        if (mounted) {
          setIsLoadingAuth(false);
          isFetchingProfileRef.current = false;
        }
      }
    };

    // _oauthCode is captured at module level before URL-syncing effects can
    // overwrite window.location. Exchange it here where the auth listener is ready.
    if (_oauthCode) {
      supabase.auth.exchangeCodeForSession(_oauthCode)
        .then(({ error }) => { if (error) console.error('[auth] code exchange failed:', error.message); })
        .catch(console.error);
    }

    // Use onAuthStateChange exclusively — it fires INITIAL_SESSION immediately,
    // which replaces the need for a separate getSession() call and eliminates
    // the race condition between initializeAuth and the subscription handler.
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, newSession) => {
      if (!mounted) return;

      setSession(newSession);

      // Only trigger the spinner + profile fetch for real auth events.
      // TOKEN_REFRESHED, USER_UPDATED, etc. must NOT re-trigger loading,
      // otherwise a hung fetchProfile + safety timeout creates an infinite loop.
      if (event === 'SIGNED_IN' || event === 'INITIAL_SESSION') {
        if (newSession) {
          const currentProfile = userProfileRef.current;
          if (!currentProfile || currentProfile.id !== newSession.user.id) {
            if (!isFetchingProfileRef.current) {
              setIsLoadingAuth(true);
              await fetchProfile(newSession);
            }
          } else {
            // Profile already loaded for this user
            setIsLoadingAuth(false);
          }
        } else {
          // INITIAL_SESSION with no session — user is not logged in
          setIsLoadingAuth(false);
        }
      } else if (event === 'SIGNED_OUT') {
        resetPostHog();
        clearSentryUser();
        setUserProfile(null);
        setCurrentOrg(null);
        setMyOrgs([]);
        setIsLoadingOrgs(false);
        setIsLoadingAuth(false);
        isFetchingProfileRef.current = false;
      }
      // TOKEN_REFRESHED / USER_UPDATED: session already updated via setSession above, no spinner needed
    });

    return () => {
      mounted = false;
      isFetchingProfileRef.current = false;
      subscription.unsubscribe();
    };
  }, []);

  const fetchMyOrgs = async (userId: string, selectOrgId?: string) => {
    // Try full query with new columns; fall back to legacy columns if migration hasn't run
    const tryQuery = async (withNewCols: boolean) => {
      const selectStr = withNewCols
        ? 'role, is_admin, status, organizations(id, name, join_code, approved_at, is_beta_tester, beta_approval_date, beta_expires_at, match_credits_used, stripe_subscription_status, unit_preference)'
        : 'role, organizations(id, name, join_code)';
      const query = supabase
        .from('organization_members')
        .select(selectStr)
        .eq('user_id', userId);
      const result = withNewCols
        ? query.or('status.eq.active,status.is.null')
        : query;
      return result;
    };

    try {
      let { data, error } = await tryQuery(true);

      if (error) {
        // New columns probably don't exist yet — fall back to legacy query
        console.warn('fetchMyOrgs: new columns not found, trying legacy query', error.message);
        ({ data, error } = await tryQuery(false));
      }

      if (data && !error) {
        const orgs = data.map((item: any) => {
          // If is_admin column exists, use it; otherwise derive from legacy roles
          const isAdmin = item.is_admin != null
            ? item.is_admin
            : (item.role === 'head_coach' || item.role === 'assistant');
          return mapOrganizationFromDB(item.organizations, item.role, isAdmin);
        });
        setMyOrgs(orgs);
        if (orgs.length > 0) {
          const target = selectOrgId ? orgs.find(o => o.id === selectOrgId) : null;
          if (target) {
            setIsLoadingData(true);
            setCurrentOrg(target);
          } else if (!currentOrg) {
            setIsLoadingData(true);
            setCurrentOrg(orgs[0]);
          }
        }
      }
    } catch (e) {
      console.error('Error fetching orgs', e);
    } finally {
      setIsLoadingOrgs(false);
    }
  };

  const handleDevBypass = () => {
    if (!import.meta.env.DEV) return; // offline/demo mode only available in development
    const mockUser = {
      id: 'dev-bypass-id',
      email: 'dev@sideoutiq.com',
      user_metadata: { full_name: 'Dev Coach' }
    };
    setSession({ access_token: 'mock-token', user: mockUser });
    setUserProfile({ id: mockUser.id, email: mockUser.email, fullName: mockUser.user_metadata.full_name });
    const mockOrg: Organization = { id: 'dev-org-id', name: 'Offline / Demo Mode', joinCode: 'OFFLINE', role: 'head_coach', isAdmin: true };
    setMyOrgs([mockOrg]);
    setCurrentOrg(mockOrg);
    setIsLoadingAuth(false);
  };

  // 2. Fetch Data (Filtered by Current Org)
  useEffect(() => {
    if (!currentOrg) return;

    const fetchData = async () => {
      if (currentOrg.id === 'dev-org-id') {
        try {
          const safeParse = (key: string) => {
            const raw = localStorage.getItem(key);
            if (!raw) return [];
            try { return JSON.parse(raw); } catch (e) { console.error(`Failed to parse ${key}`, e); return []; }
          };

          setTeams(safeParse('sideoutiq_dev_teams'));
          setPlayers(safeParse('sideoutiq_dev_players'));
          setEvents(safeParse('sideoutiq_dev_events'));
          setLineups(safeParse('sideoutiq_dev_lineups'));
          setIsDevDataLoaded(true);
          setIsLoadingData(false);
        } catch (e) {
          console.error("Critical error in dev data load", e);
          setIsDevDataLoaded(true);
          setIsLoadingData(false);
        }
        return;
      }

      setIsDevDataLoaded(false);
      setIsLoadingData(true);
      // Clear stale data immediately so a previous org's rows never bleed into the new org
      setTeams([]);
      setPlayers([]);
      setEvents([]);
      setLineups([]);
      setMatchRecords([]);
      setSeasons([]);
      setViewingSeason(null);
      try {
        const { data: teamsData } = await supabase.from('teams').select('*').eq('organization_id', currentOrg.id).neq('is_archived', true);
        if (teamsData) setTeams(teamsData.map(mapTeamFromDB));

        const { data: playersData } = await supabase.from('players').select('*').eq('organization_id', currentOrg.id);
        if (playersData) setPlayers(playersData.map(mapPlayerFromDB));

        const { data: eventsData } = await supabase.from('events').select('*').eq('organization_id', currentOrg.id);
        if (eventsData) setEvents(eventsData.map(mapEventFromDB));

        const { data: lineupsData, error: lineupsError } = await supabase.from('lineups').select('*').eq('organization_id', currentOrg.id);
        if (lineupsError) console.error('Failed to load lineups:', lineupsError.message, lineupsError.code);
        if (lineupsData) setLineups(lineupsData.map(mapLineupFromDB));

        const records = await getMatchRecords(currentOrg.id);
        setMatchRecords(records);

        const { data: seasonsData } = await supabase.from('seasons').select('*').eq('organization_id', currentOrg.id).order('created_at', { ascending: false });
        if (seasonsData) setSeasons(seasonsData.map(mapSeasonFromDB));

      } catch (error) {
        console.error("Error connecting to Supabase data:", error);
      } finally {
        setIsLoadingData(false);
      }
    };
    fetchData();
  }, [currentOrg]);

  // 2b. Fetch pending approvals count for admins
  useEffect(() => {
    if (!currentOrg?.isAdmin) return;
    getPendingApprovalCount(currentOrg.id).then(count => {
      setPendingApprovalsCount(count);
      if (count > 0 && !hasShownPendingPopupRef.current) {
        hasShownPendingPopupRef.current = true;
        setShowPendingPopup(true);
      }
    });
  }, [currentOrg]);

  // Initialize global active team — prefer Supabase (cross-device), then localStorage, then defaults
  useEffect(() => {
    if (teams.length === 0 || globalActiveTeamId) return;
    const clubTeams = teams.filter(t => (t.type === 'club' || !t.type));
    const activeId = currentOrg ? localStorage.getItem(`sideoutiq_active_team_${currentOrg.id}`) : null;
    const resolve = (supabaseTeamId: string | null) => {
      const fav = (supabaseTeamId ? clubTeams.find(t => t.id === supabaseTeamId) : null)
               || (activeId ? clubTeams.find(t => t.id === activeId) : null)
               || clubTeams[0];
      if (fav) setGlobalActiveTeamId(fav.id);
    };
    if (userProfile?.id && currentOrg?.id && currentOrg.id !== 'dev-org-id') {
      getPreferredTeamId(userProfile.id, currentOrg.id).then(resolve).catch(() => resolve(null));
    } else {
      resolve(null);
    }
  }, [teams, globalActiveTeamId, currentOrg, userProfile?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Persist global active team selection to localStorage and Supabase
  useEffect(() => {
    if (!globalActiveTeamId || !currentOrg) return;
    localStorage.setItem(`sideoutiq_active_team_${currentOrg.id}`, globalActiveTeamId);
    if (userProfile?.id && currentOrg.id !== 'dev-org-id') {
      setPreferredTeamId(userProfile.id, currentOrg.id, globalActiveTeamId).catch(() => {});
    }
  }, [globalActiveTeamId, currentOrg, userProfile?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // 3. Save Data (Dev Mode Only)
  useEffect(() => {
    if (currentOrg?.id === 'dev-org-id' && isDevDataLoaded) {
      try {
        localStorage.setItem('sideoutiq_dev_teams', JSON.stringify(teams));
        localStorage.setItem('sideoutiq_dev_players', JSON.stringify(players));
        localStorage.setItem('sideoutiq_dev_events', JSON.stringify(events));
        localStorage.setItem('sideoutiq_dev_lineups', JSON.stringify(lineups));
      } catch (e) {
        console.error("Failed to save to localStorage", e);
      }
    }
  }, [teams, players, events, lineups, currentOrg, isDevDataLoaded]);

  const handleSignOut = async () => {
    setIsSigningOut(true);
    setSession(null);
    setUserProfile(null);
    setCurrentOrg(null);
    setMyOrgs([]);
    try { await supabase.auth.signOut(); } catch (e) {}
    window.location.href = '/';
  };

  if (isLoadingAuth || isLoadingOrgs || isLoadingData) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!session) {
    return <Auth onDevBypass={handleDevBypass} hasJoinCode={!!pendingJoinCode} defaultMode={defaultAuthMode} />;
  }

  if (!userProfile) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-4">
        <div className="bg-white p-6 rounded-xl shadow-lg max-w-sm w-full text-center border border-red-100">
           <LogOut className="w-12 h-12 text-red-500 mx-auto mb-4" />
           <h3 className="font-bold text-slate-900 mb-2 text-lg">Profile Load Error</h3>
           <p className="text-slate-500 text-sm mb-6">Connection failed. Please retry.</p>
           <button onClick={handleSignOut} className="bg-slate-900 text-white px-4 py-3 rounded-lg font-bold w-full">Sign Out & Retry</button>
        </div>
      </div>
    );
  }

  if (!currentOrg) {
    const isOAuthUser = session?.user?.app_metadata?.provider !== 'email';
    if (isOAuthUser && myOrgs.length === 0) {
      return (
        <BetaAccessRequest
          user={userProfile}
          onOrgCreated={(id) => fetchMyOrgs(userProfile.id, id)}
          onSignOut={handleSignOut}
        />
      );
    }
    return (
      <div className="relative">
        <button
          onClick={handleSignOut}
          className="absolute top-4 right-4 z-50 flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 transition-colors"
        >
          <LogOut className="w-4 h-4" />
          Sign out
        </button>
        <OrgOnboarding
          user={userProfile}
          onOrgSelected={(id) => id && fetchMyOrgs(userProfile.id, id)}
          availableOrgs={myOrgs}
          onBypass={handleDevBypass}
          prefilledJoinCode={pendingJoinCode || undefined}
        />
      </div>
    );
  }

  // Org created but not yet approved by super-admin
  if (currentOrg && currentOrg.approvedAt === null && currentOrg.id !== 'dev-org-id') {
    return (
      <PendingApprovalScreen
        orgName={currentOrg.name}
        userId={userProfile.id}
        onSignOut={handleSignOut}
      />
    );
  }

  const handleTourComplete = () => {
    if (!userProfile?.id || !currentOrg?.id) return;
    const next = { ...onboardingState, welcomeComplete: true, tourComplete: true };
    saveOnboardingState(userProfile.id, currentOrg.id, next);
    setOnboardingState(next);
  };

  const handleTourSkip = () => {
    if (!userProfile?.id || !currentOrg?.id) return;
    const next = { ...onboardingState, welcomeComplete: true };
    saveOnboardingState(userProfile.id, currentOrg.id, next);
    setOnboardingState(next);
  };

  const handleMarkSection = (section: OnboardingSection) => {
    if (!userProfile?.id || !currentOrg?.id) return;
    const next = markSectionShown(userProfile.id, currentOrg.id, section, onboardingState);
    setOnboardingState(next);
  };

  const handleRestartTour = () => {
    if (!userProfile?.id || !currentOrg?.id) return;
    resetOnboarding(userProfile.id, currentOrg.id);
    setOnboardingState({ welcomeComplete: false, sectionsShown: [], tourComplete: false, demoGameDone: false });
  };


  const sectionForView = (view: ViewState): OnboardingSection | null => {
    switch (view) {
      case ViewState.TEAM: return 'team';
      case ViewState.SCHEDULE: return 'schedule';
      case ViewState.GAME: return 'gametime';
      case ViewState.STATS: return 'stats';
      case ViewState.PRACTICE: return 'practice';
      case ViewState.SETTINGS:
      case ViewState.ORGANIZATION: return 'org';
      default: return null;
    }
  };

  const renderContent = () => {
    const section = sectionForView(currentView);
    const showSectionBanner = section !== null && !onboardingState.sectionsShown.includes(section);

    const banner = showSectionBanner ? (
      <SectionBanner
        section={section!}
        onDismiss={() => handleMarkSection(section!)}
      />
    ) : null;

    switch (currentView) {
      case ViewState.DASHBOARD:
        return <Dashboard organizationName={currentOrg.name} currentOrg={currentOrg} teams={teams} players={players} events={events} matchRecords={matchRecords} changeView={setCurrentView} onPlayGame={(id) => { setActiveGameEventId(id); setCurrentView(ViewState.GAME); }} setTeams={setTeams} setPlayers={setPlayers} setEvents={setEvents} pendingApprovalsCount={pendingApprovalsCount} onGoToApprovals={() => { setOrgSettingsInitialTab('access'); setCurrentView(ViewState.ORGANIZATION); }} />;
      case ViewState.TEAM:
        return <>{banner}<TeamManagement organizationId={currentOrg.id} teams={teams} players={players} lineups={lineups} events={events} setTeams={setTeams} setPlayers={setPlayers} setLineups={setLineups} shouldAutoCreateTeam={shouldAutoCreateTeam} setShouldAutoCreateTeam={setShouldAutoCreateTeam} allowTwoLiberos={appSettings.allowTwoLiberos} onAllowTwoLiberosChange={(val) => setAppSettings(prev => ({ ...prev, allowTwoLiberos: val }))} currentOrg={currentOrg} currentUserEmail={userProfile?.email} initialActiveTeamId={globalActiveTeamId} onActiveTeamChange={setGlobalActiveTeamId} unitSystem={effectiveUnitSystem} seasons={seasons} setSeasons={setSeasons} onViewingSeasonChange={setViewingSeason} /></>;
      case ViewState.GAME:
        return <>{banner}<GameTime organizationId={currentOrg.id} currentUser={userProfile} players={players} teams={teams} events={events} lineups={lineups} matchRecords={matchRecords} setPlayers={setPlayers} setLineups={setLineups} setEvents={setEvents} setTeams={setTeams} setMatchRecords={setMatchRecords} changeView={setCurrentView} triggerTeamCreation={() => setShouldAutoCreateTeam(true)} appSettings={appSettings} setAppSettings={setAppSettings} initialMatchId={activeGameEventId} initialActiveTeamId={globalActiveTeamId} onActiveTeamChange={setGlobalActiveTeamId} /></>;
      case ViewState.STATS:
        return <>{banner}<Stats players={players} teams={teams} events={events} matchRecords={matchRecords} setMatchRecords={setMatchRecords} currentOrg={currentOrg} currentUser={userProfile} setEvents={setEvents} initialEventId={activeStatsEventId} onEditingChange={setStatsIsEditing} registerLeaveRequest={(fn) => { statsLeaveRequestRef.current = fn; }} changeView={setCurrentView} appSettings={appSettings} activeTeamId={globalActiveTeamId} /></>;
      case ViewState.PRACTICE:
        return <>{banner}<PracticeDrills events={events} matchRecords={matchRecords} players={players} teams={teams} organizationId={currentOrg.id} teamId={globalActiveTeamId || undefined} currentOrgRole={currentOrg.role} initialDate={activePracticeDate} onEditingChange={setPracticeIsDirty} registerLeaveRequest={(fn) => { practiceLeaveRequestRef.current = fn; }} changeView={setCurrentView} appSettings={appSettings} setAppSettings={setAppSettings} /></>;
      case ViewState.SCHEDULE:
        return <>{banner}<Schedule organizationId={currentOrg.id} currentOrg={currentOrg} teams={teams} events={events} matchRecords={matchRecords} setMatchRecords={setMatchRecords} setEvents={setEvents} changeView={setCurrentView} onViewStats={(id) => { setActiveStatsEventId(id); setCurrentView(ViewState.STATS); }} onPlayGame={(id) => { setActiveGameEventId(id); setCurrentView(ViewState.GAME); }} onOpenPractice={(date) => { setActivePracticeDate(date); setCurrentView(ViewState.PRACTICE); }} activeTeamId={globalActiveTeamId} seasons={seasons} setSeasons={setSeasons} /></>;
      case ViewState.ORGANIZATION:
      case ViewState.SETTINGS:
        return <>{banner}<OrgSettings organization={currentOrg} currentUser={userProfile} appSettings={appSettings} setAppSettings={setAppSettings} onUpdateUser={(name) => setUserProfile(p => p ? { ...p, fullName: name } : p)} onUpdateOrg={(updates) => setCurrentOrg(prev => prev ? { ...prev, ...updates } : prev)} initialTab={orgSettingsInitialTab as any} onRestartTour={handleRestartTour} setTeams={setTeams} /></>;
      default:
        return <Dashboard organizationName={currentOrg.name} currentOrg={currentOrg} teams={teams} players={players} events={events} matchRecords={matchRecords} changeView={setCurrentView} onPlayGame={(id) => { setActiveGameEventId(id); setCurrentView(ViewState.GAME); }} setTeams={setTeams} setPlayers={setPlayers} setEvents={setEvents} />;
    }
  };

  return (
    <div className="flex h-screen overflow-hidden bg-white">
      <Sidebar
        currentView={currentView}
        setView={handleSetView}
        isMobileMenuOpen={isMobileMenuOpen}
        setIsMobileMenuOpen={setIsMobileMenuOpen}
        schoolLogo={null}
        setSchoolLogo={() => {}}
        user={userProfile}
        currentOrg={currentOrg}
        onSwitchOrg={() => setCurrentOrg(null)}
        onSignOut={handleSignOut}
        teams={teams.filter(t => t.type === 'club' || !t.type)}
        activeTeamId={globalActiveTeamId}
        setActiveTeamId={setGlobalActiveTeamId}
      />
      <main className="flex-1 flex flex-col h-full overflow-hidden w-full relative">
        <div className="lg:hidden bg-white border-b border-slate-200 h-12 flex items-center px-4 gap-3 shrink-0 z-40">
          <button onClick={() => setIsMobileMenuOpen(true)} className="p-1 -ml-1 text-slate-600 rounded-lg hover:bg-slate-100 transition-colors">
            <Menu className="w-5 h-5" />
          </button>
          <div className="font-bold text-sm truncate text-slate-800">Sideout<span className="text-orange-500">IQ</span></div>
          {teams.filter(t => t.type === 'club' || !t.type).length > 0 && (
            <div className="relative ml-auto shrink-0 mr-[104px]">
              <select
                value={globalActiveTeamId}
                onChange={e => setGlobalActiveTeamId(e.target.value)}
                className="appearance-none bg-blue-600 text-white text-xs font-bold pl-3 pr-7 py-1.5 rounded-lg outline-none cursor-pointer"
              >
                {teams.filter(t => t.type === 'club' || !t.type).map(t => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
              <ChevronDown className="w-3 h-3 text-white/80 absolute right-2 top-2.5 pointer-events-none" />
            </div>
          )}
        </div>
        <div className="flex-1 overflow-y-auto p-4 md:p-8 pb-20 md:pb-8">
          <div className="max-w-6xl mx-auto">
            <BetaBanner org={currentOrg} />
            {viewingSeason && !viewingSeason.isActive && (
              <div className="flex items-center gap-3 bg-amber-500 text-white rounded-xl px-4 py-2.5 mb-4 text-sm font-semibold">
                <AlertTriangle className="w-4 h-4 shrink-0" />
                <span className="flex-1">Viewing past season: <strong>{viewingSeason.name}</strong></span>
                <button type="button" onClick={() => setViewingSeason(null)} className="shrink-0 underline hover:no-underline text-white">
                  Return to current
                </button>
              </div>
            )}
            {renderContent()}
          </div>
        </div>
      </main>

      <FeedbackButton userEmail={userProfile?.email} userId={userProfile?.id} userName={userProfile?.fullName} />

      {/* CSAT survey — once per 60 days, only for paid users who've used 2+ credits */}
      {cookieConsent !== null && onboardingState.welcomeComplete && currentOrg && userProfile?.id && (
        <CsatBanner org={currentOrg} userId={userProfile.id} userEmail={userProfile.email} />
      )}

      {/* Unit preference onboarding — shown once per org, blocks until confirmed */}
      {showUnitModal && currentOrg && (
        <UnitPreferenceModal
          organizationId={currentOrg.id}
          onConfirm={(system) => {
            setShowUnitModal(false);
            // Apply the chosen system as the org preference locally
            setAppSettings(prev => ({ ...prev, measurementSystem: system }));
          }}
        />
      )}

      {/* First-time welcome tour — only render after onboarding state is confirmed from storage */}
      {!showUnitModal && isOnboardingStateReady && !onboardingState.welcomeComplete && (
        <WelcomeTourModal onComplete={handleTourComplete} onSkip={handleTourSkip} />
      )}

      {/* Cookie consent banner — shown on first visit, above all other overlays */}
      {cookieConsent === null && (
        <CookieBanner onConsent={(accepted) => setCookieConsent(accepted ? 'accepted' : 'declined')} />
      )}

      {/* Pending approvals login popup */}
      {showPendingPopup && (
        <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/40 p-4 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-sm animate-fade-in-up">
            <div className="flex items-start gap-4 mb-4">
              <div className="w-11 h-11 bg-red-100 rounded-xl flex items-center justify-center shrink-0">
                <Bell className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h3 className="font-bold text-lg text-slate-900">
                  {pendingApprovalsCount} pending approval{pendingApprovalsCount !== 1 ? 's' : ''}
                </h3>
                <p className="text-sm text-slate-500 mt-0.5">
                  {pendingApprovalsCount === 1
                    ? 'A coach has requested to join your organization and needs your approval.'
                    : `${pendingApprovalsCount} coaches have requested to join your organization and need your approval.`}
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setShowPendingPopup(false)}
                className="flex-1 py-2.5 text-slate-500 font-bold hover:bg-slate-50 rounded-xl transition-colors text-sm"
              >
                Later
              </button>
              <button
                onClick={() => {
                  setShowPendingPopup(false);
                  setOrgSettingsInitialTab('access');
                  setCurrentView(ViewState.ORGANIZATION);
                }}
                className="flex-1 bg-red-600 text-white font-bold py-2.5 rounded-xl hover:bg-red-700 transition-all active:scale-95 flex items-center justify-center gap-2 text-sm"
              >
                Review Now <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default App;
